/**
 * Logger dev-only para o fluxo de auth entre portal-senne-exames
 * (origem da sessão) e portal-senneliquor (consumidor via cookie SSO).
 *
 * Habilitado por padrão quando `import.meta.env.DEV` é true.
 * Para ligar/desligar manualmente em runtime no DevTools:
 *
 *   localStorage.setItem("senne:authDebug", "1")  // força ON
 *   localStorage.setItem("senne:authDebug", "0")  // força OFF
 *   localStorage.removeItem("senne:authDebug")    // volta ao default (DEV)
 *
 * Paleta quente (laranja/vermelho/âmbar) para distinguir do operacional.
 */

const isEnabled = (): boolean => {
  try {
    const flag = localStorage.getItem("senne:authDebug");
    if (flag === "1") return true;
    if (flag === "0") return false;
  } catch {
    // ignore
  }
  return Boolean(import.meta.env.DEV);
};

export type AuthScope =
  | "bootstrap"
  | "csrf"
  | "newApi"
  | "sync"
  | "logout"
  | "refresh";

const STYLES: Record<AuthScope, string> = {
  bootstrap: "color:#c2410c;font-weight:bold",
  csrf: "color:#ea580c;font-weight:bold",
  newApi: "color:#b45309;font-weight:bold",
  sync: "color:#d97706;font-weight:bold",
  logout: "color:#b91c1c;font-weight:bold",
  refresh: "color:#dc2626;font-weight:bold",
};

const tag = (scope: AuthScope) =>
  [`%c[auth:${scope}]`, STYLES[scope] || "color:#000"];

export const authLogger = {
  isEnabled,

  log(scope: AuthScope, msg: string, data?: unknown) {
    if (!isEnabled()) return;
    if (data !== undefined) {
      // eslint-disable-next-line no-console
      console.log(...tag(scope), msg, data);
    } else {
      // eslint-disable-next-line no-console
      console.log(...tag(scope), msg);
    }
  },

  warn(scope: AuthScope, msg: string, data?: unknown) {
    if (!isEnabled()) return;
    if (data !== undefined) {
      // eslint-disable-next-line no-console
      console.warn(...tag(scope), msg, data);
    } else {
      // eslint-disable-next-line no-console
      console.warn(...tag(scope), msg);
    }
  },

  error(scope: AuthScope, msg: string, data?: unknown) {
    // sempre loga erros, mesmo com debug off
    if (data !== undefined) {
      // eslint-disable-next-line no-console
      console.error(...tag(scope), msg, data);
    } else {
      // eslint-disable-next-line no-console
      console.error(...tag(scope), msg);
    }
  },

  group(scope: AuthScope, label: string) {
    if (!isEnabled()) return () => undefined;
    // eslint-disable-next-line no-console
    console.groupCollapsed(...tag(scope), label);
    return () => {
      // eslint-disable-next-line no-console
      console.groupEnd();
    };
  },

  /**
   * Snapshot do que o JS consegue ver de cookies. Útil para diagnóstico
   * inicial — refreshToken/sessionFingerprint/csrfToken são httpOnly e
   * NÃO aparecem aqui (esse é o ponto). Ver presença via DevTools >
   * Application > Cookies > localhost.
   */
  cookieSnapshot(): { documentCookie: string; visible: string[] } {
    const doc = typeof document !== "undefined" ? document.cookie : "";
    const visible = doc
      .split(";")
      .map((c): string => c.trim().split("=")[0] ?? "")
      .filter(Boolean);
    return { documentCookie: doc, visible };
  },
};

export default authLogger;
